import { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { prepareBannerImageUpload } from "../utils/bannerImage";

/**
 * Validate and read a banner image File, returning the prepared result.
 * Throws with a user-readable message if the file is invalid.
 * Extracted to allow components with different state shapes (e.g. FundGroups) to
 * share the same validation logic without duplicating try/catch boilerplate.
 */
export const processBannerImageFile = async (file) => {
  if (!file) return null;
  return prepareBannerImageUpload(file);
};

/**
 * Shared hook that manages banner image selection, validation, and preview state.
 * Eliminates the duplicated handler logic in CreateCampaign, EditCampaign, and FundGroups.
 */
const useBannerImageUpload = () => {
  const [bannerImageDataUrl, setBannerImageDataUrl] = useState("");
  const [bannerImagePreview, setBannerImagePreview] = useState("");
  const [bannerImageFileName, setBannerImageFileName] = useState("");
  const [bannerImageWarning, setBannerImageWarning] = useState("");
  const toast = useToast();

  const handleBannerImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setBannerImageDataUrl("");
      setBannerImagePreview("");
      setBannerImageFileName("");
      setBannerImageWarning("");
      return;
    }

    try {
      const prepared = await prepareBannerImageUpload(file);
      setBannerImageDataUrl(prepared.dataUrl);
      setBannerImagePreview(prepared.dataUrl);
      setBannerImageFileName(prepared.fileName);
      setBannerImageWarning(prepared.warnings?.[0] || "");
    } catch (error) {
      setBannerImageDataUrl("");
      setBannerImagePreview("");
      setBannerImageFileName("");
      setBannerImageWarning("");
      e.target.value = "";
      toast({
        title: "Invalid Banner Image",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetBannerImage = useCallback(() => {
    setBannerImageDataUrl("");
    setBannerImagePreview("");
    setBannerImageFileName("");
    setBannerImageWarning("");
  }, []);

  return {
    bannerImageDataUrl,
    bannerImagePreview,
    bannerImageFileName,
    bannerImageWarning,
    handleBannerImageChange,
    resetBannerImage,
  };
};

export default useBannerImageUpload;
